import { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { ErrorNotice, Loading, Notice, PageHeading, Stat } from '../../components/ui';
import { useMeta, useRun } from '../../hooks/useEngine';
import { dateTime, pct, usd, usdT } from '../../lib/format';
import { useScenario } from '../../state/scenario';
import { ScenarioPanel } from '../scenario/ScenarioPanel';
import { OptionDetail } from './OptionDetail';
import { OptionsTable } from './OptionsTable';
import { VerdictBanner } from './VerdictBanner';

export function DecisionPage() {
  const meta = useMeta();
  const { state, dispatch, runRequest } = useScenario();
  const run = useRun(runRequest);
  const [selected, setSelected] = useState<string | null>(null);

  const result = run.data?.result;
  const focusKey = `${state.scenario.location}:${state.scenario.route}`;
  const selectedKey = selected && result?.results.some((r) => r.key === selected) ? selected : result?.results.some((r) => r.key === focusKey) ? focusKey : result?.recommended.key;
  const option = result?.results.find((r) => r.key === selectedKey);

  // Keep the focus location (used by Sensitivity/Uncertainty/Advisor) in step with the row the user inspects.
  useEffect(() => {
    if (!selected || !result) return;
    const o = result.results.find((r) => r.key === selected);
    if (o && (o.location !== state.scenario.location || o.route !== state.scenario.route)) {
      dispatch({ type: 'patch', patch: { location: o.location, route: o.route } });
    }
  }, [selected, result, state.scenario.location, state.scenario.route, dispatch]);

  return (
    <>
      <PageHeading
        eyebrow="Spodumene → lithium hydroxide"
        title="Where should the plant go, and does it pay?"
        description="Ranks candidate sites and process routes on breakeven price, returns, cost, carbon and risk. Every number traces to a graded assumption."
        action={run.isFetching && <span className="row small muted"><LoaderCircle className="spin" size={14} aria-hidden />Recalculating</span>}
      />
      <div className="decision-grid">
        <aside>{meta.data ? <ScenarioPanel meta={meta.data.meta} /> : meta.isError ? <ErrorNotice error={meta.error} retry={() => void meta.refetch()} /> : <Loading label="Loading model…" />}</aside>
        <div>
          {run.data?.offline && (
            <Notice tone="warn">
              <strong>Offline snapshot.</strong> The API is unreachable, so this is the pre-computed base case{run.data.snapshotDate ? ` from ${dateTime(run.data.snapshotDate)}` : ''}. Your inputs are not applied until the connection returns.
            </Notice>
          )}
          <ErrorNotice error={run.error} retry={() => void run.refetch()} />
          {!result && run.isPending && <Loading />}
          {result && (
            <>
              <VerdictBanner run={result} />
              {option && (
                <div className="stats">
                  <Stat label="Breakeven (selected)" value={usdT(option.breakeven_lioh_price_usd_t)} sub={`${option.location_name}`} />
                  <Stat label="Project IRR" value={pct(option.irr)} sub={`hurdle ${pct(option.wacc, 0)}`} />
                  <Stat label="NPV at WACC" value={usd(option.npv_usd)} sub={`net capex ${usd(option.capex.net)}`} />
                  <Stat label="All-in cost" value={usdT(option.all_in_cost_usd_t)} sub={`feed ${pct(option.cost_breakdown_usd_t['Feed (SC6)'] / option.cash_cost_usd_t, 0)} of cash cost`} />
                </div>
              )}
              <OptionsTable run={result} selected={selectedKey ?? ''} onSelect={setSelected} />
              {option && <OptionDetail option={option} />}
            </>
          )}
        </div>
      </div>
    </>
  );
}
